import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom";
import { FiLogIn, FiUserPlus, FiX } from "react-icons/fi";
import axios from "axios";
import config from "../config";
import { useAuth } from "../context/AuthContext";
import PaymentModal from "../components/PaymentModal";
import { Search, X } from "lucide-react"; // Icon imports
import { Helmet } from 'react-helmet-async';
import { toast } from "react-toastify";

const ITEMS_PER_PAGE = 12;

const Bids = () => {
    const navigate = useNavigate()
    const eventTitle = process.env.REACT_APP_EVENT_TITLE || 'EventSawa';
    const eventDescription = process.env.REACT_APP_EVENT_DESCRIPTION || 'We make events sawa';
    const [bids, setBids] = useState([]);
    const [selectedBid, setSelectedBid] = useState(null);
    const [userBids, setUserBids] = useState({});
    const [filteredBids, setFilteredBids] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const { user, isLoggedIn } = useAuth();
    const [showModal, setShowModal] = useState(false);
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [showPricePopup, setShowPricePopup] = useState(false);
    const togglePricePopup = () => setShowPricePopup((prev) => !prev);

    useEffect(() => {
        axios.get(`${config.API_BASE_URL}/bids/open/`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` }
        })
            .then(res => {
                setBids(res.data);
                setFilteredBids(res.data);
                const userBidStatus = {};
                res.data.forEach(bid => {
                    userBidStatus[bid.id] = (
                        (typeof bid.service_providers === 'string' && bid.service_providers.split(",").includes(String(user?.id))) ||
                        (Array.isArray(bid.service_providers) && bid.service_providers.includes(user?.id))
                    ) || false;
                });
                setUserBids(userBidStatus);
            })
            .catch(error => {
                if (error.response?.status === 403) setShowModal(true);
                // else console.error("Error fetching bids:", error);
                else console.error("Error fetching bids:");
            });
    }, [user]);

    useEffect(() => {
        const results = bids.filter(bid => {
            const matchesSearch =
                bid.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                bid.description.toLowerCase().includes(searchTerm.toLowerCase());

            const bidPrice = parseFloat(bid.price); // adjust field name if needed

            const matchesPrice =
                (!minPrice || bidPrice >= parseFloat(minPrice)) &&
                (!maxPrice || bidPrice <= parseFloat(maxPrice));

            return matchesSearch && matchesPrice;
        });

        setFilteredBids(results);
        setCurrentPage(1);
    }, [searchTerm, bids, minPrice, maxPrice]);


    const paginatedBids = filteredBids.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const totalPages = Math.ceil(filteredBids.length / ITEMS_PER_PAGE);

    useEffect(() => {
        if (!selectedBid) return; // Ensure selectedBid is defined before continuing

        // Now you can safely use selectedBid
        const storedBidStatus = localStorage.getItem(`bidStatus_${selectedBid.id}`);
        if (storedBidStatus === "review") {
            setUserBids(prev => ({
                ...prev,
                [selectedBid.id]: "review" // Set the bid status as 'review'
            }));
        }
    }, [selectedBid]);

    const handlePlaceBid = (bidId) => {
        if (!isLoggedIn || !user?.id) return;

        const applicantId = user.id;
        const hasBid = userBids[bidId] === "review" || userBids[bidId] === true;

        const action = hasBid ? "withdraw" : "place";
        const payload = hasBid
            ? { event: bidId }
            : { applicant_id: applicantId };

        // console.log(`Attempting to ${action} bid`, payload);

        axios.post(`${config.API_BASE_URL}/bids/${bidId}/${action}/`, payload, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
        })
            .then(() => {
                // ✅ Update all bids
                setBids(prev =>
                    prev.map(bid =>
                        bid.id === bidId
                            ? {
                                ...bid,
                                bids_count: Math.max(0, bid.bids_count + (hasBid ? -1 : 1))
                            }
                            : bid
                    )
                );

                // ✅ Update selectedBid if it's the one being modified
                setSelectedBid(prev =>
                    prev?.id === bidId
                        ? {
                            ...prev,
                            bids_count: Math.max(0, prev.bids_count + (hasBid ? -1 : 1))
                        }
                        : prev
                );

                // ✅ Update userBids
                setUserBids(prev => ({
                    ...prev,
                    [bidId]: hasBid ? null : "review"
                }));

                // ✅ Sync localStorage
                if (hasBid) {
                    localStorage.removeItem(`bidStatus_${bidId}`);
                } else {
                    localStorage.setItem(`bidStatus_${bidId}`, "review");
                }

                // console.log(`${action === 'withdraw' ? 'Withdrawn' : 'Placed'} bid for`, bidId);
            })
            .catch(error => {
                // console.error(`Error trying to ${action} bid:`, error.response?.data || error.message);
            });
    };

    return (
        <div className="p-6 text-center">
            {isLoggedIn && user?.role === "service_provider" ? (
                <div style={{ padding: "2rem" }}>
                    <Helmet>
                        <title>{eventTitle}</title> {/* Set page title from environment variable */}
                        <meta name="description" content={eventDescription} />
                    </Helmet>
                    <h2 style={{ fontSize: "26px", marginBottom: "1.5rem", fontWeight: "600" }}>Open Bids</h2>

                    {showModal && <PaymentModal onClose={() => setShowModal(false)} />}


                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "1rem",
                            marginBottom: "2rem",
                            padding: "1.5rem",
                            background: "#ffffff",
                            borderRadius: "16px",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
                            border: "1px solid #f0f0f0",
                            fontFamily: "'Poppins', sans-serif",
                        }}
                    >
                        {/* Search Input */}
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "1rem",
                                marginBottom: "2rem",
                                padding: "1.5rem",
                                background: "#ffffff",
                                borderRadius: "16px",
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
                                border: "1px solid #f0f0f0",
                                fontFamily: "'Poppins', sans-serif",
                            }}
                        >
                            {/* Search Input with price filter toggle */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.75rem",
                                    position: "relative",
                                }}
                            >
                                <Search size={20} color="#777" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search by title or description"
                                    onFocus={togglePricePopup}
                                    style={{
                                        flex: 1,
                                        padding: "12px 16px",
                                        fontSize: "1rem",
                                        borderRadius: "12px",
                                        border: "1px solid #dcdcdc",
                                        backgroundColor: "#f9f9f9",
                                        outline: "none",
                                        transition: "all 0.2s ease-in-out",
                                    }}
                                />
                                {showPricePopup && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: "50px",
                                            left: "0",
                                            width: "100%",
                                            backgroundColor: "#ffffff",
                                            borderRadius: "12px",
                                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                                            padding: "1rem",
                                            zIndex: 10,
                                            display: "flex",
                                            gap: "1rem",
                                        }}
                                    >
                                        <input
                                            type="number"
                                            placeholder="Min Price"
                                            value={minPrice}
                                            onChange={(e) => setMinPrice(e.target.value)}
                                            style={{
                                                flex: 1,
                                                padding: "12px 16px",
                                                fontSize: "1rem",
                                                borderRadius: "12px",
                                                border: "1px solid #dcdcdc",
                                                backgroundColor: "#f9f9f9",
                                                outline: "none",
                                                transition: "all 0.2s ease-in-out",
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.border = "1px solid #4CAF50";
                                                e.target.style.backgroundColor = "#fff";
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.border = "1px solid #dcdcdc";
                                                e.target.style.backgroundColor = "#f9f9f9";
                                            }}
                                        />
                                        <input
                                            type="number"
                                            placeholder="Max Price"
                                            value={maxPrice}
                                            onChange={(e) => setMaxPrice(e.target.value)}
                                            style={{
                                                flex: 1,
                                                padding: "12px 16px",
                                                fontSize: "1rem",
                                                borderRadius: "12px",
                                                border: "1px solid #dcdcdc",
                                                backgroundColor: "#f9f9f9",
                                                outline: "none",
                                                transition: "all 0.2s ease-in-out",
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.border = "1px solid #FF5722";
                                                e.target.style.backgroundColor = "#fff";
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.border = "1px solid #dcdcdc";
                                                e.target.style.backgroundColor = "#f9f9f9";
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {filteredBids.length === 0 ? (
                        <p>No open bids available.</p>
                    ) : (
                        <>
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                                gap: "1rem"
                            }}>
                                {paginatedBids.map(bid => (
                                    <div
                                        key={bid.id}
                                        onClick={() => setSelectedBid(bid)}
                                        style={{
                                            backgroundColor: "#fff",
                                            padding: "16px",
                                            borderRadius: "12px",
                                            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                                            cursor: "pointer",
                                            transition: "transform 0.2s",
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.02)"}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                                    >
                                        <h4 style={{
                                            marginBottom: "8px",
                                            fontWeight: "600",
                                            fontSize: "1.05rem",
                                            color: "#222",
                                            lineHeight: "1.3",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            display: "block",
                                            fontFamily: "'Poppins', sans-serif"
                                        }}>
                                            {bid.title}
                                        </h4>

                                        <p style={{
                                            marginBottom: "8px",
                                            fontWeight: "600",
                                            color: "#E65100",
                                            fontSize: "1rem",
                                            letterSpacing: "0.4px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            textShadow: "1px 1px 4px rgba(0, 0, 0, 0.05)",
                                            fontFamily: "'Poppins', sans-serif"
                                        }}>
                                            <span role="img" aria-label="coin">🪙</span>
                                            <span>KSh. {bid.price}</span>
                                        </p>

                                        <div style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            margintop: "8px",
                                            marginBottom: "8px"
                                        }}>
                                            <p style={{
                                                fontSize: "0.85rem",
                                                color: bid.bids_count ? "#666" : "#E65100",
                                                fontWeight: "500",
                                                fontFamily: "'Poppins', sans-serif",
                                                margin: 0
                                            }}>
                                                {bid.bids_count
                                                    ? `Bids: ${bid.bids_count === 1 ? '1 bid' : `${bid.bids_count} bids`}`
                                                    : (
                                                        <span style={{
                                                            display: "inline-block",
                                                            padding: "4px 10px",
                                                            fontSize: "0.72rem",
                                                            fontWeight: "600",
                                                            color: "#fff",
                                                            background: "linear-gradient(135deg, #ff6f00, #e65100)",
                                                            borderRadius: "20px",
                                                            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                                                            textTransform: "uppercase",
                                                            letterSpacing: "0.8px",
                                                            fontFamily: "'Poppins', sans-serif",
                                                            cursor: "default"
                                                        }}>
                                                            No bids yet
                                                        </span>
                                                    )
                                                }
                                            </p>

                                            <span style={{
                                                fontSize: "0.78rem",
                                                fontWeight: "500",
                                                color: "#2E7D32",
                                                backgroundColor: "#E8F5E9",
                                                padding: "5px 10px",
                                                borderRadius: "8px",
                                                fontFamily: "'Poppins', sans-serif",
                                                letterSpacing: "0.5px",
                                                display: "inline-block",
                                                whiteSpace: "nowrap"
                                            }}>
                                                💸 Place Your Bid
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {totalPages > 1 && (
                                <div style={{ marginTop: "2rem", display: "flex", justifyContent: "center", gap: "10px" }}>
                                    {Array.from({ length: totalPages }, (_, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setCurrentPage(i + 1)}
                                            style={{
                                                padding: "6px 12px",
                                                border: "1px solid #ccc",
                                                borderRadius: "6px",
                                                backgroundColor: currentPage === i + 1 ? "#2196F3" : "#fff",
                                                color: currentPage === i + 1 ? "#fff" : "#333",
                                                cursor: "pointer",
                                            }}
                                        >
                                            {i + 1}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {selectedBid && (
                        <div
                            style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                backgroundColor: "rgba(0, 0, 0, 0.4)",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                zIndex: 1000,
                            }}
                            onClick={() => setSelectedBid(null)}
                        >
                            <div
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                    background: "#fff",
                                    padding: "24px",
                                    borderRadius: "10px",
                                    width: "100%",
                                    maxWidth: "500px",
                                    boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                                    position: "relative"
                                }}
                            >
                                <button
                                    onClick={() => setSelectedBid(null)}
                                    style={{
                                        position: "absolute",
                                        top: "10px",
                                        right: "10px",
                                        background: "none",
                                        border: "none",
                                        fontSize: "20px",
                                        color: "#888",
                                        cursor: "pointer"
                                    }}
                                ><X /></button>

                                <div style={{ fontFamily: "'Poppins', sans-serif", color: "#333" }}>
                                    <h3 style={{
                                        fontSize: "1.2rem",
                                        fontWeight: "600",
                                        marginBottom: "8px",
                                        color: "#222"
                                    }}>
                                        {selectedBid.title}
                                    </h3>

                                    <p style={{
                                        fontSize: "0.95rem",
                                        lineHeight: "1.6",
                                        marginBottom: "12px",
                                        color: "#555"
                                    }}>
                                        {selectedBid.description}
                                    </p>

                                    <div style={{ fontFamily: "'Poppins', sans-serif", color: "#333" }}>
                                        <div style={{ display: "flex", marginBottom: "6px", fontSize: "0.9rem", fontWeight: "500", color: "#E65100" }}>
                                            <span style={{ minWidth: "110px", display: "inline-block" }}>Budget:</span>
                                            <span>KSh. {selectedBid.price}</span>
                                        </div>

                                        <div style={{ display: "flex", marginBottom: "6px", fontSize: "0.9rem", fontWeight: "500", color: "#1E88E5" }}>
                                            <span style={{ minWidth: "110px", display: "inline-block" }}>Event date:</span>
                                            {/* <span>{selectedBid.deadline}</span> */}
                                            <span>{new Date(selectedBid.deadline).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                                        </div>
                                        <div style={{
                                            display: "flex",
                                            fontSize: "0.9rem",
                                            fontWeight: "500",
                                            color: selectedBid.bids_count ? "#4E342E" : "#E65100"
                                        }}>
                                            <span style={{ minWidth: "110px", display: "inline-block" }}>Bids:</span>
                                            <span>
                                                {selectedBid.bids_count === 1
                                                    ? "1 bid"
                                                    : `${selectedBid.bids_count || 0} bids`}
                                            </span>
                                        </div>

                                    </div>
                                </div>
                                {selectedBid?.created_by_id !== user?.id && (
                                    <div style={{ marginTop: "25px", fontFamily: "'Poppins', sans-serif" }}>
                                        {userBids[selectedBid.id] === "review" ? (
                                            <>
                                                <p style={{
                                                    fontSize: "1rem",
                                                    fontWeight: "500",
                                                    color: "#E65100",
                                                    marginBottom: "10px",
                                                    textAlign: "center",
                                                }}>
                                                    Bid Under Review
                                                </p>
                                                <button
                                                    onClick={() => handlePlaceBid(selectedBid.id)}
                                                    style={{
                                                        width: "100%",
                                                        padding: "12px",
                                                        backgroundColor: "#f44336",
                                                        color: "#fff",
                                                        border: "none",
                                                        borderRadius: "6px",
                                                        cursor: "pointer",
                                                        fontWeight: "500",
                                                        fontSize: "1rem"
                                                    }}
                                                >
                                                    Withdraw Bid
                                                </button>
                                            </>
                                        ) : userBids[selectedBid.id] ? (
                                            <p style={{
                                                fontSize: "1rem",
                                                fontWeight: "500",
                                                color: "#4CAF50",
                                                marginBottom: "10px",
                                                textAlign: "center",
                                            }}>
                                                Bid Submitted, Under Review
                                            </p>
                                        ) : (
                                            <button
                                                onClick={() => handlePlaceBid(selectedBid.id)}
                                                style={{
                                                    width: "100%",
                                                    padding: "12px",
                                                    backgroundColor: "#4CAF50",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: "6px",
                                                    cursor: "pointer",
                                                    fontWeight: "500",
                                                    fontSize: "1rem"
                                                }}
                                            >
                                                Place Bid
                                            </button>
                                        )}

                                        {selectedBid.status === "accepted" && (
                                            <button
                                                onClick={() => alert("Contact organizer clicked")}
                                                style={{
                                                    marginTop: "15px",
                                                    width: "100%",
                                                    padding: "12px",
                                                    backgroundColor: "#2196F3",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: "6px",
                                                    cursor: "pointer",
                                                    fontWeight: "500",
                                                    fontSize: "1rem"
                                                }}
                                            >
                                                Contact Organizer
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            ) : isLoggedIn && user?.role !== "service_provider" ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] space-y-8">
                    <div className="text-center">
                        <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                            Service Provider Required
                        </h2>
                        <p className="mt-2 text-gray-600 max-w-md mx-auto">
                            Only verified service providers can bid on events. Please log in with your service provider account to access bidding features.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <button
                            onClick={() => navigate('/login')}
                            className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                        >
                            <FiLogIn className="w-5 h-5 mr-2" />
                            Sign In as Service Provider
                        </button>
                        <button
                            onClick={() => navigate('/register')}
                            className="flex items-center justify-center px-6 py-3 bg-white text-indigo-600 border-2 border-indigo-600 rounded-xl shadow hover:bg-indigo-50 transition-all duration-300"
                        >
                            <FiUserPlus className="w-5 h-5 mr-2" />
                            Register as Service Provider
                        </button>
                    </div>
                    <div className="flex flex-col items-center space-y-4">
                        <p className="text-gray-500">
                            Already have an account?{' '}
                            <button
                                onClick={() => navigate('/login')}
                                className="text-indigo-600 hover:text-indigo-700 font-medium"
                            >
                                Sign in here
                            </button>
                        </p>
                        <p className="text-sm text-gray-500">
                            By creating an account, you agree to our{' '}
                            <a href="/terms" className="text-indigo-600 hover:text-indigo-700">Terms of Service</a>
                            {' '}and{' '}
                            <a href="/privacy" className="text-indigo-600 hover:text-indigo-700">Privacy Policy</a>
                        </p>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center min-h-[400px] space-y-8">
                    <div className="text-center">
                        <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                            Welcome to EventSawa
                        </h2>
                        <p className="mt-2 text-gray-600 max-w-md mx-auto">
                            Connect with event organizers and showcase your services. Join our community of professional service providers today!
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <button
                            onClick={() => navigate('/login')}
                            className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                        >
                            <FiLogIn className="w-5 h-5 mr-2" />
                            Sign In
                        </button>
                        <button
                            onClick={() => navigate('/register')}
                            className="flex items-center justify-center px-6 py-3 bg-white text-indigo-600 border-2 border-indigo-600 rounded-xl shadow hover:bg-indigo-50 transition-all duration-300"
                        >
                            <FiUserPlus className="w-5 h-5 mr-2" />
                            Create Account
                        </button>
                    </div>
                    <div className="flex flex-col items-center space-y-4">
                        <p className="text-gray-500">
                            Already have an account?{' '}
                            <button
                                onClick={() => navigate('/login')}
                                className="text-indigo-600 hover:text-indigo-700 font-medium"
                            >
                                Sign in here
                            </button>
                        </p>
                        <p className="text-sm text-gray-500">
                            By creating an account, you agree to our{' '}
                            <a href="/terms" className="text-indigo-600 hover:text-indigo-700">Terms of Service</a>
                            {' '}and{' '}
                            <a href="/privacy" className="text-indigo-600 hover:text-indigo-700">Privacy Policy</a>
                        </p>
                    </div>
                </div>
            )}
        </div>

    );
};

export default Bids;